import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import TreeMark from "@/components/TreeMark";
import PrivacyNotice from "@/components/PrivacyNotice";
import PasswordInput from "@/components/PasswordInput";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      setErr("Please tick the box to confirm you agree to the personal-use terms.");
      return;
    }
    setErr(""); setLoading(true);
    try {
      await register(name, email, password);
      navigate("/dashboard", { replace: true });
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16 md:py-24">
      <TreeMark size={56} tone="dark" className="mb-6" />
      <div className="text-xs uppercase tracking-[0.2em] text-[#6B7F62] mb-3">Welcome</div>
      <h1 className="font-serif text-4xl text-[#2D332D] mb-2">Create your account</h1>
      <p className="text-[#576056] mb-10">Free, private, and you can export or delete everything anytime.</p>

      <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
        <div>
          <label className="block text-sm font-medium text-[#2D332D] mb-2">Your name</label>
          <input data-testid="register-name" type="text" className="input" value={name}
                 onChange={(e) => setName(e.target.value)} required autoFocus
                 placeholder="e.g. Sarah" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#2D332D] mb-2">Email</label>
          <input data-testid="register-email" type="email" className="input" value={email}
                 onChange={(e) => setEmail(e.target.value)} required
                 placeholder="you@example.com" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#2D332D] mb-2">Password</label>
          <PasswordInput
            testId="register-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            placeholder="At least 6 characters"
            autoComplete="new-password"
          />
        </div>
        {err && <div data-testid="register-error" className="text-sm text-[#B05A5A] bg-[#B05A5A]/8 border border-[#B05A5A]/25 rounded-xl px-4 py-3">{err}</div>}

        <label className="flex items-start gap-3 cursor-pointer group" data-testid="register-terms-label">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            data-testid="register-terms-checkbox"
            className="mt-1 w-4 h-4 rounded border-[#D4CEC4] text-[#6B7F62] focus:ring-[#9EAD94] cursor-pointer flex-shrink-0"
            required
          />
          <span className="text-xs text-[#576056] leading-relaxed">
            By joining, you're agreeing to use this material for your own personal guidance and support only. Please do not copy, share, or redistribute premium content, downloads, or resources outside your own household without written permission. This helps protect the work involved in creating these resources and allows this service to stay sustainable for others who need it.
          </span>
        </label>

        <button data-testid="register-submit" type="submit" className="btn-primary w-full justify-center" disabled={loading || !agreed}>
          {loading ? "Creating your account…" : "Create account"}
        </button>
        <PrivacyNotice variant="inline" className="mt-2" />
      </form>

      <div className="mt-8 text-sm text-[#576056]">
        Already have an account? <Link to="/login" data-testid="to-login" className="text-[#6B7F62] font-medium hover:underline">Sign in</Link>
      </div>
    </div>
  );
}
